import { useEffect, useRef, useState } from 'react';
import { Phone } from 'lucide-react';
import { RefillApiError, refillApi, type RefillPageInfo, type RefillStarted } from '../../lib/api';
import { useI18n, type MessageKey } from '../../lib/i18n';
import { dobProblem, isUsablePhone, type FieldErrors } from '../../lib/validate';
import { Alert, Button, Field, Input, Spinner, StepActions, SuccessMark, buttonClasses } from '../../components/ui';
import { StepBar } from '../../components/StepBar';
import { useShellBrand } from '../../components/Shell';
import { CODE_LENGTH, CodeBoxes } from '../../components/CodeBoxes';
import { DateOfBirth, EMPTY_DOB, isoFromParts, type DobParts } from '../forms/DateOfBirth';
import { Turnstile } from '../forms/Turnstile';
import { RequestStep, type KeptRequest } from './RequestStep';
import { StepHeading } from './StepHeading';
import { maskedPhone, telHref } from './refill-request';

/** Another code may be asked for this long after the last (ORX-D3). */
export const RESEND_AFTER_MS = 60_000;

type Load = 'loading' | 'ready' | 'missing' | 'offline' | 'error';
type Step = 'who' | 'code' | 'request' | 'done';
/** Why the code step cannot go on: too many wrong codes, the code ran out, or the request did. */
type Stopped = 'locked' | 'expired' | 'session';
/** `spam` is the robot check: the server's `spam_check_failed` is said by its box. */
type WhoField = 'firstName' | 'lastName' | 'dob' | 'phone' | 'spam';
/** What the done step says: the reference, and how "ready" will reach the patient. */
type DoneDetails = { reference: string; textWhenReady: boolean; email: string | null; textsStopped: boolean };

const STEPS: MessageKey[] = ['refill.step.who', 'refill.step.code', 'refill.step.request', 'refill.step.done'];
const STEP_INDEX: Record<Step, number> = { who: 0, code: 1, request: 2, done: 3 };
/** ORX-D16 — no code step where the pharmacy asks for none. */
const STEPS_NO_CODE: MessageKey[] = ['refill.step.who', 'refill.step.request', 'refill.step.done'];
const STEP_INDEX_NO_CODE: Record<Step, number> = { who: 0, code: 0, request: 1, done: 2 };

const WHO_IDS: Record<WhoField, string> = {
  firstName: 'refill-first',
  lastName: 'refill-last',
  dob: 'refill-dob',
  phone: 'refill-phone',
  spam: 'refill-spam',
};

/** The longest name the server takes (RxManager_BE `NAME_MAX`). */
const NAME_MAX = 80;

const STOPPED_WORDS: Record<Stopped, MessageKey> = {
  locked: 'refill.locked',
  expired: 'refill.expired',
  session: 'refill.sessionExpired',
};

/**
 * Track ORX — a request for a refill, a renewal or a transfer, on the pharmacy's booking site
 * (`/<slug>/r`, `/r` on its own subdomain, `/embed/<slug>/r` in its website's frame).
 *
 * Who → Code → Request → Done. The patient says who they are; a 6-digit code texted to their mobile
 * proves the number is theirs (ORX-D3); then they say what they need, and it lands in the
 * pharmacy's refill queue beside phone and text requests (ORX-D1).
 *
 * ## Nothing here says whether a person is on file (ORX-D5)
 *
 * Every name, birth date and number gets the same "We texted a code" — the server answers alike for
 * anyone, and so does the page.
 *
 * ## Nothing leaves the page but the requests themselves
 *
 * Names, the number, the code and the medications are never put in the address, the console, the
 * browser's storage or anything else. A reload starts over, which is the point.
 *
 * Where the pharmacy cannot text, or has switched online requests off (ORX-D10, D11), the page says
 * "Call us at …" instead.
 *
 * ## Without a code (Track ORX follow-ups, ORX-D15 to D20)
 *
 * Where `GET :slug` says `verification: 'none'`, the steps are Who → Request → Done: the start asks
 * for no code (`verification: 'none'`) and answers the submit token at once. The server still
 * decides, so a start that answers no token goes on to the code step as before. With no code to
 * prove the number, the request step shows it to check, with "Change number" back to the first
 * step. An optional email, for this request only, works either way.
 *
 * ## A mobile that once texted STOP (ORX-D24)
 *
 * No text reaches it. With a code, the start (or a resend) is 409 `texts_stopped`: said on the
 * mobile number (or on the code step), never as the whole page's "call us", so the patient can
 * reply START or use another number and press the same button again. Without a code, the start
 * says `textsStopped: true`: the request step offers no "Text me when it's ready" and asks for an
 * email instead, and the done step promises no text.
 */
export function RefillPage({ slug }: { slug: string }) {
  const { t } = useI18n();
  const [load, setLoad] = useState<Load>('loading');
  const [info, setInfo] = useState<RefillPageInfo | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [step, setStep] = useState<Step>('who');
  const [stopped, setStopped] = useState<Stopped | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [submitToken, setSubmitToken] = useState<string | null>(null);
  const [done, setDone] = useState<DoneDetails | null>(null);
  // Who they are: kept when they start again, so a new code is one press away.
  const [who, setWho] = useState<WhoDetails>(EMPTY_WHO);
  /** The page asked for no code and the server texted one anyway: the code flow from then on. */
  const [codeAfterAll, setCodeAfterAll] = useState(false);
  /** The submit token came from the start, so no code proved the number (ORX-D19). */
  const [phoneUnchecked, setPhoneUnchecked] = useState(false);
  /** The request step's fields while the patient changes their number. */
  const [kept, setKept] = useState<KeptRequest | null>(null);
  /** ORX-D24 — the start said this mobile once texted STOP: no "ready" text can reach it. */
  const [textsStopped, setTextsStopped] = useState(false);

  useShellBrand(info ? { name: info.pharmacyName, phone: info.phone ?? null } : null);

  useEffect(() => {
    if (!slug) {
      setLoad('missing');
      return;
    }
    let cancelled = false;
    setLoad('loading');
    refillApi
      .info(slug)
      .then((result) => {
        if (cancelled) return;
        setInfo(result);
        setUnavailable(!result.available);
        setLoad('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const code = error instanceof RefillApiError ? error.code : 'unknown';
        setLoad(code === 'missing' ? 'missing' : code === 'offline' ? 'offline' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    // The pharmacy's name, never the patient's or what they asked for: a tab list is not private.
    if (info) document.title = `${info.pharmacyName} — ${t('refill.title')}`;
  }, [info, t]);

  if (load === 'loading') return <Spinner label={t('refill.loading')} />;
  if (load !== 'ready' || !info) return <LoadProblem kind={load === 'ready' ? 'error' : load} />;
  if (unavailable) return <CallUs phone={info.phone ?? null} />;

  // An older server says nothing, which is the code flow (ORX-D16).
  const noCode = info.verification === 'none' && !codeAfterAll;
  const steps = noCode ? STEPS_NO_CODE : STEPS;
  const stepIndex = noCode ? STEP_INDEX_NO_CODE : STEP_INDEX;

  const startAgain = (): void => {
    setStopped(null);
    setChallengeId(null);
    setSubmitToken(null);
    setPhoneUnchecked(false);
    setTextsStopped(false);
    setStep('who');
  };

  const started = ({ challengeId: id, submitToken: token, textsStopped: stopped }: RefillStarted): void => {
    setChallengeId(id);
    // ORX-D24 — only a plain `true`, and only with the token: absent (an older server) is as
    // before, and a code that went out after all reached the number.
    setTextsStopped(Boolean(token) && stopped === true);
    if (token) {
      // ORX-D17 — no code: straight on to the request, with the number to check.
      setSubmitToken(token);
      setPhoneUnchecked(true);
      setStep('request');
      return;
    }
    // A code went out after all (the switch is off here, or the server is older).
    if (noCode) setCodeAfterAll(true);
    setStep('code');
  };

  return (
    <div className="space-y-6">
      <StepBar labels={steps.map((key) => t(key))} current={stepIndex[step]} />

      {stopped ? (
        <StoppedNotice reason={stopped} noCode={noCode} onStartAgain={startAgain} />
      ) : step === 'who' ? (
        <WhoStep
          slug={slug}
          info={info}
          noCode={noCode}
          who={who}
          onWho={setWho}
          onStarted={started}
          onUnavailable={() => setUnavailable(true)}
        />
      ) : step === 'code' && challengeId ? (
        <CodeStep
          challengeId={challengeId}
          phone={who.phone}
          pharmacy={info.pharmacyName}
          onVerified={(token) => {
            setSubmitToken(token);
            setStep('request');
          }}
          onStopped={setStopped}
          onChangeDetails={startAgain}
          onUnavailable={() => setUnavailable(true)}
        />
      ) : step === 'request' && submitToken ? (
        <RequestStep
          info={info}
          submitToken={submitToken}
          phoneToCheck={phoneUnchecked ? who.phone : undefined}
          textsStopped={textsStopped}
          kept={kept ?? undefined}
          onChangeNumber={(fields) => {
            // Back to who they are, fields kept; the token is dropped, so Continue starts again.
            setKept(fields);
            startAgain();
          }}
          onDone={(result) => {
            setDone({ ...result, textsStopped });
            setKept(null);
            setSubmitToken(null);
            setStep('done');
          }}
          onExpired={() => setStopped('session')}
          onUnavailable={() => setUnavailable(true)}
        />
      ) : done ? (
        <Done
          pharmacy={info.pharmacyName}
          reference={done.reference}
          textWhenReady={done.textWhenReady}
          email={done.email}
          textsStopped={done.textsStopped}
        />
      ) : null}
    </div>
  );
}

// ── Step 1: who they are ──────────────────────────────────────────────────────

type WhoDetails = { firstName: string; lastName: string; dob: DobParts; phone: string };
const EMPTY_WHO: WhoDetails = { firstName: '', lastName: '', dob: EMPTY_DOB, phone: '' };

function WhoStep({
  slug,
  info,
  noCode,
  who,
  onWho,
  onStarted,
  onUnavailable,
}: {
  slug: string;
  info: RefillPageInfo;
  /** ORX-D16 — the pharmacy asks for no code: other words, and the start says so. */
  noCode: boolean;
  who: WhoDetails;
  onWho: (next: WhoDetails) => void;
  onStarted: (started: RefillStarted) => void;
  onUnavailable: () => void;
}) {
  const { t, locale } = useI18n();
  const [website, setWebsite] = useState('');
  const [turnstile, setTurnstile] = useState('');
  // A Turnstile token is spent by the attempt that carried it: a fresh widget for the next one.
  const [turnstileRound, setTurnstileRound] = useState(0);
  const [errors, setErrors] = useState<FieldErrors<WhoField>>({});
  const [refusal, setRefusal] = useState<MessageKey | null>(null);
  const [busy, setBusy] = useState(false);
  const summary = useRef<HTMLDivElement>(null);
  const siteKey = info.turnstileSiteKey ?? null;
  const set = (changes: Partial<WhoDetails>): void => onWho({ ...who, ...changes });
  // A field's words, with the pharmacy's name where they use it (ORX-D24's `texts_stopped`).
  const say = (key: MessageKey): string => t(key, { pharmacy: info.pharmacyName });

  const start = async (): Promise<void> => {
    const found: FieldErrors<WhoField> = {};
    // Sign-off Q2 (2026-10-30): one message per field — the page's own check and the server's say
    // the same thing.
    if (!who.firstName.trim()) found.firstName = 'refill.nameMissing';
    if (!who.lastName.trim()) found.lastName = 'refill.nameMissing';
    const dob = isoFromParts(who.dob);
    const dobWrong = dob ? dobProblem(dob) : 'form.dobInvalid';
    if (dobWrong) found.dob = dobWrong;
    if (!who.phone.trim()) found.phone = 'error.fieldRequired';
    else if (!isUsablePhone(who.phone)) found.phone = 'refill.phoneInvalid';
    setErrors(found);
    setRefusal(null);
    if (Object.keys(found).length > 0 || !dob) {
      setTimeout(() => summary.current?.focus(), 0);
      return;
    }
    if (siteKey && !turnstile) {
      setRefusal('form.spamFailed');
      setTimeout(() => summary.current?.focus(), 0);
      return;
    }
    setBusy(true);
    try {
      const result = await refillApi.start(slug, {
        firstName: who.firstName.trim(),
        lastName: who.lastName.trim(),
        dob,
        phone: who.phone.trim(),
        language: locale,
        ...(website ? { website } : {}),
        ...(turnstile ? { turnstileToken: turnstile } : {}),
        // Only when the page was told it may: the server still decides (ORX-D16).
        ...(noCode ? { verification: 'none' as const } : {}),
      });
      onStarted(result);
    } catch (error) {
      const code = error instanceof RefillApiError ? error.code : 'unknown';
      if (code === 'not_available') {
        onUnavailable();
        return;
      }
      // The server's own checks of who they are, said by the field they are about.
      const said = whoRefusal(code, who, Boolean(siteKey));
      if (said) {
        setErrors(said);
      } else {
        setRefusal(
          code === 'too_many'
            ? noCode
              ? 'refill.tooManyNoCode'
              : 'refill.tooMany'
            : code === 'invalid'
              ? 'refill.checkDetails'
              : code === 'offline'
                ? 'error.offline'
                : 'error.unknown',
        );
      }
      if (turnstile || code === 'spam_check_failed') {
        setTurnstile('');
        setTurnstileRound((round) => round + 1);
      }
      setTimeout(() => summary.current?.focus(), 0);
    } finally {
      setBusy(false);
    }
  };

  const fieldErrors = Object.entries(errors) as [WhoField, MessageKey][];

  return (
    <form
      noValidate
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        void start();
      }}
    >
      <div className="space-y-2">
        <StepHeading>{t('refill.title')}</StepHeading>
        <p className="text-lg text-foreground">{t(noCode ? 'refill.whoLeadNoCode' : 'refill.whoLead')}</p>
      </div>

      {(fieldErrors.length > 0 || refusal) && (
        <div ref={summary} tabIndex={-1} className="focus:outline-none">
          <Alert>
            {refusal ? (
              t(refusal)
            ) : (
              <>
                {t('error.summary')}
                <ul className="mt-1 list-disc pl-5">
                  {fieldErrors.map(([name, key]) => (
                    <li key={name}>
                      <a
                        href={`#${WHO_IDS[name]}`}
                        onClick={(event) => {
                          event.preventDefault();
                          // The birth date's first box, or the field itself.
                          (document.getElementById(WHO_IDS[name]) ??
                            document.querySelector<HTMLInputElement>(`#${WHO_IDS[name]} input`))?.focus();
                        }}
                        className="text-danger underline underline-offset-4"
                      >
                        {say(key)}
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Alert>
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          id={WHO_IDS.firstName}
          label={t('details.firstName')}
          required
          requiredLabel={t('details.required')}
          error={errors.firstName && t(errors.firstName)}
        >
          {(props) => (
            <Input
              {...props}
              autoComplete="given-name"
              value={who.firstName}
              onChange={(event) => set({ firstName: event.target.value })}
            />
          )}
        </Field>
        <Field
          id={WHO_IDS.lastName}
          label={t('details.lastName')}
          required
          requiredLabel={t('details.required')}
          error={errors.lastName && t(errors.lastName)}
        >
          {(props) => (
            <Input
              {...props}
              autoComplete="family-name"
              value={who.lastName}
              onChange={(event) => set({ lastName: event.target.value })}
            />
          )}
        </Field>
      </div>
      <div id={WHO_IDS.dob}>
        <DateOfBirth
          value={who.dob}
          onChange={(dob) => set({ dob })}
          error={errors.dob ? t(errors.dob) : null}
          legend={t('details.dob')}
          hint={t('form.dobHint')}
        />
      </div>
      <Field
        id={WHO_IDS.phone}
        label={t('details.phone')}
        help={t(noCode ? 'refill.mobileHelpNoCode' : 'refill.mobileHelp')}
        required
        requiredLabel={t('details.required')}
        error={errors.phone && say(errors.phone)}
      >
        {(props) => (
          <Input
            {...props}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={who.phone}
            onChange={(event) => set({ phone: event.target.value })}
          />
        )}
      </Field>

      {/* For bots only: people never see it, and a filled one is refused. */}
      <div aria-hidden="true" className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="refill-website">Website</label>
        <input
          id="refill-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
        />
      </div>

      {siteKey && (
        <div id={WHO_IDS.spam} tabIndex={-1} className="space-y-2 focus:outline-none">
          <p className="text-base font-medium text-foreground">{t('form.spamCheck')}</p>
          <Turnstile key={turnstileRound} siteKey={siteKey} language={locale} onToken={setTurnstile} />
          {errors.spam && <p className="text-sm font-medium text-danger">{t(errors.spam)}</p>}
        </div>
      )}

      <StepActions>
        <Button type="submit" loading={busy} className="flex-1 sm:flex-none">
          {t(noCode ? 'step.continue' : 'refill.sendCode')}
        </Button>
      </StepActions>
    </form>
  );
}

/**
 * The start's refusals about who they are (found 2026-10-28, when they read "Something went
 * wrong"), as field errors; `null` for any other refusal. A name the server calls missing or too
 * long is marked where the page can tell which, and on both names where it cannot.
 */
function whoRefusal(code: string, who: WhoDetails, hasSpamCheck: boolean): FieldErrors<WhoField> | null {
  switch (code) {
    case 'phone_invalid':
      return { phone: 'refill.phoneInvalid' };
    case 'texts_stopped':
      // ORX-D24 — the number once texted STOP: another number, or START and the same button again.
      return { phone: 'refill.textsStoppedCode' };
    case 'dob_invalid':
      // Sign-off Q2: the page's own, clearer words for a date of birth.
      return { dob: 'form.dobInvalid' };
    case 'name_missing':
      return { firstName: 'refill.nameMissing', lastName: 'refill.nameMissing' };
    case 'name_too_long': {
      const first = who.firstName.trim().length > NAME_MAX;
      const last = who.lastName.trim().length > NAME_MAX;
      return {
        ...(first || !last ? { firstName: 'refill.nameTooLong' as const } : {}),
        ...(last || !first ? { lastName: 'refill.nameTooLong' as const } : {}),
      };
    }
    case 'spam_check_failed':
      // By the box, where there is one to try again; without one the patient can do nothing here.
      return hasSpamCheck ? { spam: 'form.spamFailed' } : null;
    default:
      return null;
  }
}

// ── Step 2: the code ─────────────────────────────────────────────────────────

/** Whole seconds until `at`, ticking once a second until it arrives. */
function useSecondsUntil(at: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (at <= Date.now()) return;
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= at) window.clearInterval(timer);
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [at]);
  return Math.max(0, Math.ceil((at - now) / 1_000));
}

function CodeStep({
  challengeId,
  phone,
  pharmacy,
  onVerified,
  onStopped,
  onChangeDetails,
  onUnavailable,
}: {
  challengeId: string;
  phone: string;
  /** The pharmacy's name, for `texts_stopped`'s "If you replied STOP to …". */
  pharmacy: string;
  onVerified: (submitToken: string) => void;
  onStopped: (reason: Stopped) => void;
  onChangeDetails: () => void;
  onUnavailable: () => void;
}) {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  /** No more codes for this request: the server said so without a time to wait. */
  const [spent, setSpent] = useState(false);
  const [resendAt, setResendAt] = useState(() => Date.now() + RESEND_AFTER_MS);
  const secondsLeft = useSecondsUntil(resendAt);
  const box = useRef<HTMLInputElement>(null);

  const refused = (failure: unknown): void => {
    const reason = failure instanceof RefillApiError ? failure.code : 'unknown';
    if (reason === 'locked' || reason === 'expired') return onStopped(reason);
    if (reason === 'not_available') return onUnavailable();
    // ORX-D24 — a resend to a number that texted STOP since: said here, the code step stays.
    if (reason === 'texts_stopped') return setError(t('refill.textsStoppedCode', { pharmacy }));
    setError(t(reason === 'offline' ? 'error.offline' : reason === 'too_many' ? 'refill.tooMany' : 'error.unknown'));
  };

  const verify = async (): Promise<void> => {
    setNotice(null);
    if (code.length !== CODE_LENGTH) {
      setError(t('refill.codeIncomplete'));
      box.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const { submitToken } = await refillApi.verify(challengeId, code);
      onVerified(submitToken);
    } catch (failure) {
      if (failure instanceof RefillApiError && failure.code === 'wrong_code') {
        const left = failure.triesLeft;
        if (left === 0) return onStopped('locked');
        setError(
          left === null
            ? t('refill.wrongCodeAgain')
            : left === 1
              ? t('refill.wrongCodeOne')
              : t('refill.wrongCode', { count: left }),
        );
        setCode('');
        box.current?.focus();
      } else {
        refused(failure);
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async (): Promise<void> => {
    setNotice(null);
    setError(null);
    setResending(true);
    try {
      await refillApi.resend(challengeId);
      setCode('');
      setResendAt(Date.now() + RESEND_AFTER_MS);
      setNotice(t('refill.resent'));
      box.current?.focus();
    } catch (failure) {
      if (failure instanceof RefillApiError && (failure.code === 'too_many' || failure.code === 'too_soon')) {
        // `too_soon` is a wait, never "no more codes": a minute when it does not say how long.
        const wait = failure.retryAfterSeconds ?? (failure.code === 'too_soon' ? RESEND_AFTER_MS / 1_000 : null);
        if (wait) {
          setResendAt(Date.now() + wait * 1_000);
        } else {
          setSpent(true);
          setNotice(t('refill.noMoreCodes'));
        }
      } else {
        refused(failure);
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <form
      noValidate
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        void verify();
      }}
    >
      <div className="space-y-2">
        <StepHeading>{t('refill.codeHeading')}</StepHeading>
        <p className="text-lg text-foreground">{t('refill.codeSentTo', { phone: maskedPhone(phone) })}</p>
      </div>

      {notice && <Alert tone="info">{notice}</Alert>}

      <CodeBoxes
        ref={box}
        id="refill-code"
        label={t('refill.codeLabel')}
        hint={t('refill.codeHint')}
        value={code}
        onChange={(next) => {
          setCode(next);
          if (error) setError(null);
        }}
        error={error}
      />

      <div className="space-y-2">
        <Button
          variant="outline"
          size="sm"
          disabled={spent || secondsLeft > 0}
          loading={resending}
          onClick={() => void resend()}
        >
          {t('refill.resend')}
        </Button>
        {!spent && secondsLeft > 0 && (
          <p className="text-sm text-muted-foreground">{t('refill.resendIn', { seconds: secondsLeft })}</p>
        )}
      </div>

      <StepActions>
        <Button variant="outline" onClick={onChangeDetails}>
          {t('refill.changeDetails')}
        </Button>
        <Button type="submit" loading={busy} className="flex-1 sm:flex-none">
          {t('step.continue')}
        </Button>
      </StepActions>
    </form>
  );
}

/** Locked, expired, or the request ran out: say which, and start again. */
function StoppedNotice({
  reason,
  noCode,
  onStartAgain,
}: {
  reason: Stopped;
  /** No code is texted on starting again, so the help does not promise one. */
  noCode: boolean;
  onStartAgain: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <StepHeading>{t(STOPPED_WORDS[reason])}</StepHeading>
      <p className="text-lg text-foreground">{t(noCode ? 'refill.startAgainHelpNoCode' : 'refill.startAgainHelp')}</p>
      <Button onClick={onStartAgain}>{t('refill.startAgain')}</Button>
    </div>
  );
}

// ── Step 4 (3 without a code): done ──────────────────────────────────────────

function Done({
  pharmacy,
  reference,
  textWhenReady,
  email,
  textsStopped,
}: {
  pharmacy: string;
  reference: string;
  textWhenReady: boolean;
  /** ORX-D21 — the address typed for "we got your request", or null. */
  email: string | null;
  /** ORX-D24 — no text can reach the number: "ready" comes by email, or by a call. */
  textsStopped: boolean;
}) {
  const { t } = useI18n();
  // A number that texted STOP is never promised a text, whatever the box said.
  const after: MessageKey = textsStopped
    ? email
      ? 'refill.doneEmailWhenReady'
      : 'refill.doneCall'
    : textWhenReady
      ? 'refill.doneText'
      : 'refill.doneCall';
  return (
    <div className="flex flex-col items-start gap-5">
      <SuccessMark />
      <StepHeading>{t('refill.doneHeading', { pharmacy })}</StepHeading>
      <div className="rounded-xl border border-border bg-muted/70 px-5 py-4">
        <p className="text-sm font-medium text-muted-foreground">{t('refill.reference')}</p>
        <p className="text-2xl font-semibold tracking-wide text-heading tabular-nums">{reference}</p>
      </div>
      <p className="text-lg text-foreground">{t(after)}</p>
      {email && <p className="text-lg text-foreground">{t('refill.doneEmailed', { email })}</p>}
    </div>
  );
}

// ── When there is no online request to make ──────────────────────────────────

/** The pharmacy cannot text, or has online requests switched off (ORX-D10, D11). */
function CallUs({ phone }: { phone: string | null }) {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <StepHeading>{t('refill.unavailableHeading')}</StepHeading>
      {phone ? (
        <>
          <p className="text-lg text-foreground">{t('refill.callUsAt')}</p>
          <a href={telHref(phone)} className={buttonClasses('primary', 'lg')}>
            <Phone className="h-5 w-5" aria-hidden="true" />
            {phone}
          </a>
        </>
      ) : (
        <p className="text-lg text-foreground">{t('refill.callUs')}</p>
      )}
    </div>
  );
}

function LoadProblem({ kind }: { kind: Exclude<Load, 'loading' | 'ready'> }) {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <StepHeading>
        {t(kind === 'missing' ? 'error.missing' : kind === 'offline' ? 'error.offline' : 'error.unknown')}
      </StepHeading>
      {kind === 'missing' ? (
        <p className="text-base text-muted-foreground">{t('error.missingHelp')}</p>
      ) : (
        <Button onClick={() => window.location.reload()}>{t('error.retry')}</Button>
      )}
    </div>
  );
}
